import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export interface ProductJsonLd {
  name: string;
  description: string;
  image?: string;
  category?: string;
}

interface PageMetaProps {
  title: string;
  description: string;
  ogImage?: string;
  /** Optional Product structured data for product pages */
  product?: ProductJsonLd;
}

const SITE_NAME = "Kočevar d.o.o.";
const SITE_ORIGIN = "https://kocevar.eu";
const DEFAULT_OG_IMAGE = "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/6e413fde-4746-41a7-85fb-5b121b4a9da4/id-preview-13ce5472--f33b1225-f886-4c21-a31d-ee6d5d69df9e.lovable.app-1773130809599.png";

const ORGANIZATION_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Kočevar d.o.o.",
  url: SITE_ORIGIN,
  logo: `${SITE_ORIGIN}/logos/expert-trafo.svg`,
  description: "Manufacturer of industrial resistance welding machines and equipment — spot welders, projection welders, seam welders, micro welding systems.",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Seidlova cesta 32",
    addressLocality: "Celje",
    postalCode: "3000",
    addressCountry: "SI",
  },
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "sales",
    email: "info@kocevar.eu",
    availableLanguage: ["Slovenian", "English", "German"],
  },
  sameAs: [
    "https://www.linkedin.com/company/kocevar",
    "https://www.youtube.com/@kocevar",
  ],
};

const JSONLD_ORG_ID = "jsonld-organization";
const JSONLD_PRODUCT_ID = "jsonld-product";
const JSONLD_BREADCRUMB_ID = "jsonld-breadcrumb";

const BREADCRUMB_LABELS: Record<string, string> = {
  products: "Products",
  solutions: "Solutions",
  services: "Services",
  "spare-parts": "Spare Parts & Components",
  consumables: "Consumables & Electrodes",
  accessories: "Accessories",
  "knowledge-hub": "Knowledge Hub",
  faq: "FAQ",
  testimonials: "Testimonials",
  "users-clients": "Users & Clients",
  partners: "Partners",
  "about-us": "About Us",
  "splosni-pogoji": "Privacy Policy",
};

function buildBreadcrumbJsonLd(pathname: string, pageTitle: string) {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return null;

  const items: { "@type": string; position: number; name: string; item?: string }[] = [
    { "@type": "ListItem", position: 1, name: "Home", item: SITE_ORIGIN },
  ];

  let path = "";
  for (let i = 0; i < segments.length; i++) {
    path += `/${segments[i]}`;
    const isLast = i === segments.length - 1;
    const label = isLast
      ? pageTitle
      : BREADCRUMB_LABELS[segments[i]] || segments[i].replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

    const entry: { "@type": string; position: number; name: string; item?: string } = {
      "@type": "ListItem",
      position: i + 2,
      name: label,
    };
    if (!isLast) entry.item = `${SITE_ORIGIN}${path}`;
    items.push(entry);
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

function setJsonLd(id: string, data: object) {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd(id: string) {
  document.getElementById(id)?.remove();
}

/**
 * Sets document title, meta description, Open Graph tags, canonical link,
 * and JSON-LD structured data (Organization + optional Product).
 */
const PageMeta = ({ title, description, ogImage, product }: PageMetaProps) => {
  const { pathname } = useLocation();

  useEffect(() => {
    const fullTitle = `${title} | ${SITE_NAME}`;
    document.title = fullTitle;

    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        const [attrName, attrVal] = selector
          .match(/\[([^=]+)="([^"]+)"\]/)!
          .slice(1);
        el.setAttribute(attrName, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    // Canonical URL
    const canonicalUrl = `${SITE_ORIGIN}${pathname === "/" ? "" : pathname}`;
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[property="og:title"]', "content", fullTitle);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:image"]', "content", ogImage || DEFAULT_OG_IMAGE);
    setMeta('meta[property="og:url"]', "content", canonicalUrl);
    setMeta('meta[name="twitter:title"]', "content", fullTitle);
    setMeta('meta[name="twitter:description"]', "content", description);
    setMeta('meta[name="twitter:image"]', "content", ogImage || DEFAULT_OG_IMAGE);

    // JSON-LD: Organization (always present)
    setJsonLd(JSONLD_ORG_ID, ORGANIZATION_JSONLD);

    // JSON-LD: Product (only on product pages)
    if (product) {
      setJsonLd(JSONLD_PRODUCT_ID, {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.name,
        description: product.description,
        image: product.image ? `${SITE_ORIGIN}${product.image}` : undefined,
        category: product.category,
        url: canonicalUrl,
        brand: {
          "@type": "Brand",
          name: "Kočevar",
        },
        manufacturer: {
          "@type": "Organization",
          name: "Kočevar d.o.o.",
          url: SITE_ORIGIN,
        },
      });
    } else {
      removeJsonLd(JSONLD_PRODUCT_ID);
    }

    // JSON-LD: BreadcrumbList (on all subpages)
    const breadcrumb = buildBreadcrumbJsonLd(pathname, title);
    if (breadcrumb) {
      setJsonLd(JSONLD_BREADCRUMB_ID, breadcrumb);
    } else {
      removeJsonLd(JSONLD_BREADCRUMB_ID);
    }

    return () => {
      document.title = SITE_NAME;
      removeJsonLd(JSONLD_PRODUCT_ID);
      removeJsonLd(JSONLD_BREADCRUMB_ID);
    };
  }, [title, description, ogImage, pathname, product]);

  return null;
};

export default PageMeta;
